import { DateTime } from 'luxon';

import { localeFormats } from '@/config/locale.config';
import { store } from '@/store/store';

enum DateFormat {
  ISO_8601 = 'yyyy-MM-dd',
}

export const BrowserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Format an ISO 8601 string to yyyy-MM-dd
 *
 * @param date - the ISO date string
 *
 * @example
 * toSimpleDate('2022-11-27T09:08:23.213')
 */
export const toSimpleDate = (date: string) => {
  return DateTime.fromISO(date, {zone: 'America/Los_Angeles'}).toLocal().toFormat(DateFormat.ISO_8601);
};

/**
 * Format an ISO 8601 string to LLLL dd, HH:mm:ss
 *
 * @param date - the ISO date string
 *
 * @example
 * toMonthDateTime('2022-11-27T09:08:23.213')
 */
export const toMonthDateTime = (date: string) => {
  return DateTime.fromISO(date).toFormat(localeFormats().DAY_MONTH_TIME);
};

/**
 * Convert date from an ISO 8601 string to browser's timezone and format it
 *
 * @param date - the ISO date string
 * @param format - the format string
 *
 * @example
 * covertDateToBrowsersTimezoneFormatted('2022-11-27T09:08:23.213', 'MM-DD-yyyy')
 */
export const convertDateToBrowsersTimezoneFormatted = (date: string, format: string) => {
  const dateTime = DateTime.fromISO(date, { zone: BrowserTimezone });
  return dateTime.toFormat(format);
};

/**
 * Convert date from an ISO 8601 string to another timezone anf format it
 *
 * @param date - the ISO date string
 * @param timezone - the timezone the date will be converted to
 * @param format - the format string
 *
 * @example
 * covertDateToTimezoneFormatted('2022-11-27T09:08:23.213', 'America/Denver', 'MM-DD-yyyy')
 */
export const convertDateToTimezoneFormatted = (date: string, timezone: string, format: string) => {
  const dateTime = DateTime.fromISO(date, { zone: timezone });
  return dateTime.toFormat(format);
};

/**
 * Converts a provided date to the user's timezone and applies a provided format.
 *
 * @param {string} date - Date string
 * @param {DateFormat} format  - Desired output format, defaults to ISO 8601
 *
 * @returns {null|string} - Returns formatted date in user's timezone or null
 * */
export const getTimezonedDate = (date: string, format: DateFormat = DateFormat.ISO_8601): null | string => {
  const {
    auth: { userPassport },
  } = store.getState();

  const userTz = userPassport?.user?.tz ?? BrowserTimezone;
  const isTzAEnabled = !!userPassport?.user?.parameters?.LBLiteTimeZoneAwareness;

  return isTzAEnabled
    ? convertDateToBrowsersTimezoneFormatted(date, format)
    : convertDateToTimezoneFormatted(date, userTz, format);
};

/*
 * Luxon does not have native support for day oridinals (st, th, rd) like Moment JS
 * therefore we must implement our own. If the day cannot be parsed to a number, the
 * passed date string is returned unmodified.
 *
 * @params dateString a date string in any format where the day value is wrapped with $ tokens. e.g. January $14$ 2999, 12:30:59
 *
 * */
export const getDateWithOrdinalSuffix = (dateString: string, formatString:string): string => {
  // Use the format string to identify the location of the day value
  const cleanTokenSplit = formatString.split('$').filter((item) => item);
  const indexOfDay = cleanTokenSplit.indexOf('d');

  // Split up the actual date string and grab the day
  const cleanDateSplit = dateString.split('$').filter((item) => item);
  const day = cleanDateSplit[indexOfDay];
  const numericalDay = Number.parseInt(day);

  if (Number.isNaN(numericalDay)) return dateString;

  let suffix;

  // Get our ordinal since Luxon does not provide ordinal support
  /* eslint-disable no-magic-numbers */
  if (numericalDay > 3 && numericalDay < 21) suffix = 'th';
  else {
    switch (numericalDay % 10) {
      case 1:
        suffix = 'st';
        break;
      case 2:
        suffix = 'nd';
        break;

      case 3:
        suffix = 'rd';
        break;

      default:
        suffix = 'th';
        break;
    }
  }

  let [dateFront, dateBack] = ['', ''];

  // If the day is at the start, then everything else goes after the day
  if (indexOfDay === 0) {
    dateBack = cleanDateSplit.splice(1).join('');

    return `${day}${suffix} ${dateBack}`;

    // The day is at the end so everything goes before the day
  } else if (indexOfDay === cleanDateSplit.length - 1) {
    dateFront = cleanDateSplit[0];

    return `${dateFront} ${day}${suffix}`;
  }

  // Otherwise, the day is somewhere in the middle
  dateFront = cleanDateSplit.slice(0, indexOfDay).join('');
  dateBack = cleanDateSplit.slice(indexOfDay + 1).join('');

  return `${dateFront} ${day}${suffix} ${dateBack}`;
  /* eslint-enable no-magic-numbers */
};
